import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { X, Wand2, Navigation, MapPinOff, Loader2, Store } from "lucide-react";
import { dayLabel } from "../reminders/boardUtils";

const pin = (label, bg) => L.divIcon({
  className: "",
  html: `<div style="background:${bg};color:#fff;width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4)"><span style="transform:rotate(45deg);font:900 12px Arial">${label}</span></div>`,
  iconSize: [28, 28], iconAnchor: [14, 28],
});

const FitBounds = ({ pts }) => {
  const map = useMap();
  useEffect(() => { if (pts.length) map.fitBounds(pts, { padding: [40, 40] }); }, [map, pts]);
  return null;
};

const gmapsUrl = (shop, stops) => {
  if (!stops.length) return null;
  const ll = (p) => `${p.lat},${p.lon}`;
  const way = stops.slice(0, -1).map(ll).join("|");
  return `https://www.google.com/maps/dir/?api=1&travelmode=driving&origin=${ll(shop)}&destination=${ll(stops[stops.length - 1])}${way ? `&waypoints=${encodeURIComponent(way)}` : ""}`;
};

export default function RouteMapPopup({ tech, date, onClose, onReordered }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setD(await api("GET", `/route?tech=${encodeURIComponent(tech)}&date=${date}`)); } catch (e) { toast.error(e.message); }
  }, [tech, date]);
  useEffect(() => { load(); }, [load]);

  const optimise = async () => {
    setBusy(true);
    try {
      const before = d.route.distance_km;
      const r = await api("POST", "/route/optimise", { tech, date });
      setD(r); onReordered();
      toast.success(`Run reordered — ${before} km → ${r.route.distance_km} km`);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const savePin = async (s, e) => {
    const { lat, lng } = e.target.getLatLng();
    await api("PUT", "/route/pin", { reminder_id: s.id, lat, lon: lng });
    toast.success(`Pin saved for ${s.client_name}`);
    load();
  };

  const allPts = useMemo(() => (d ? [[d.shop.lat, d.shop.lon], ...d.stops.map((s) => [s.lat, s.lon])] : []), [d]);
  const url = d && gmapsUrl(d.shop, d.stops);
  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/60 flex items-center justify-center p-2 sm:p-4" data-testid="route-map-popup">
      <div className="w-full max-w-6xl h-[92vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-200">
          <span className="font-display font-black text-slate-900">Route Map · {tech} · {dayLabel(date)}</span>
          {d && <span className="text-sm font-bold text-slate-600" data-testid="route-totals">{d.stops.length} stops · {d.route.distance_km} km · ~{d.route.duration_min} min driving</span>}
          <div className="ml-auto flex gap-2">
            <button onClick={optimise} disabled={!d || busy || d.stops.length < 2} data-testid="route-optimise-btn" className="rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}Optimise route</button>
            {url && <a href={url} target="_blank" rel="noreferrer" data-testid="route-gmaps-btn" className="rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-bold px-4 py-2 inline-flex items-center gap-1.5"><Navigation className="h-4 w-4" />Open in Google Maps</a>}
            <button onClick={onClose} data-testid="route-map-close" className="rounded-full border border-slate-200 p-2"><X className="h-4 w-4" /></button>
          </div>
        </div>
        {!d ? (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-500" data-testid="route-loading"><Loader2 className="h-8 w-8 animate-spin mb-2" />Locating addresses… (first time can take a few seconds)</div>
        ) : (
          <div className="flex-1 flex flex-col md:flex-row min-h-0">
            <div className="md:w-72 shrink-0 overflow-y-auto border-b md:border-b-0 md:border-r border-slate-200 p-3 space-y-1.5 max-h-48 md:max-h-none" data-testid="route-stop-list">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-800"><Store className="h-4 w-4" />{d.shop.label}</div>
              {d.stops.map((s, i) => (
                <div key={s.id} className="text-[13px]" data-testid="route-stop">
                  {d.route.legs[i] && <p className="text-[10px] text-slate-400 pl-7">↓ {d.route.legs[i].km} km · {d.route.legs[i].min} min</p>}
                  <p className="flex gap-2"><span className="h-5 w-5 shrink-0 rounded-full bg-sky-700 text-white text-[11px] font-black flex items-center justify-center">{i + 1}</span>
                    <span className="min-w-0"><b>{s.client_name}</b>{s.approx && <span className="ml-1 text-[10px] font-bold text-amber-600">(suburb only — drag pin)</span>}<br /><span className="text-slate-500">{s.address}</span></span></p>
                </div>
              ))}
              {d.missing.length > 0 && (
                <div className="mt-3 rounded-lg bg-red-50 border border-red-200 p-2" data-testid="route-missing">
                  <p className="text-xs font-black text-red-700 flex items-center gap-1"><MapPinOff className="h-3.5 w-3.5" />No location found ({d.missing.length})</p>
                  <p className="text-[11px] text-red-600 mb-1">Drag the red “?” pin near the shop to the right spot — it's saved for next time.</p>
                  {d.missing.map((m) => <p key={m.id} className="text-[12px] text-slate-700">• {m.client_name}</p>)}
                </div>
              )}
            </div>
            <div className="flex-1 min-h-[300px]">
              <MapContainer center={[d.shop.lat, d.shop.lon]} zoom={11} style={{ height: "100%", width: "100%" }}>
                <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <FitBounds pts={allPts} />
                <Marker position={[d.shop.lat, d.shop.lon]} icon={pin("S", "#0f172a")}><Popup>{d.shop.label}<br />{d.shop.address}</Popup></Marker>
                {d.stops.map((s, i) => (
                  <Marker key={s.id} position={[s.lat, s.lon]} icon={pin(i + 1, s.approx ? "#d97706" : "#0369a1")} draggable eventHandlers={{ dragend: (e) => savePin(s, e) }}>
                    <Popup><b>{i + 1}. {s.client_name}</b><br />{s.address}<br /><i>{s.text}</i></Popup>
                  </Marker>
                ))}
                {d.missing.map((m, i) => (
                  <Marker key={m.id} position={[d.shop.lat + 0.004 * (i + 1), d.shop.lon + 0.004 * (i + 1)]} icon={pin("?", "#dc2626")} draggable eventHandlers={{ dragend: (e) => savePin(m, e) }}>
                    <Popup>Drag me to {m.client_name}</Popup>
                  </Marker>
                ))}
                {d.route.geometry.length > 0 && <Polyline positions={d.route.geometry} pathOptions={{ color: "#0369a1", weight: 5, opacity: 0.75 }} />}
              </MapContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
